import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DailyLog } from "../types";
import { formatDateDMY } from "../format";
import "@/design-system/typography.css";
import '@/design-system/forms.css';
import { ProtectedImage } from '@/components/shared/ProtectedImage';

interface DailyLogCardProps {
  log: DailyLog;
  onOpen: (log: DailyLog) => void;
  projectIdToName?: Map<string, string>;
  isSelected?: boolean;
  showCheckbox?: boolean;
  onSelect?: (checked: boolean) => void;
}

export function DailyLogCard({ log, onOpen, projectIdToName, isSelected = false, showCheckbox = false, onSelect }: DailyLogCardProps) {
  const dateLabel = formatDateDMY(log.date);
  const projectLabel = projectIdToName?.get(log.projectId) ?? log.projectId;
  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-start justify-between">
        <div className="flex items-start gap-2">
          {showCheckbox && onSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-gray-300 mt-1"
            />
          )}
          <div>
            <CardTitle>
              <button
                type="button"
                className="text-gray-900 underline-offset-4 hover:underline"
                onClick={() => onOpen(log)}
              >
                {dateLabel}
              </button>
            </CardTitle>
          </div>
        </div>
        <div className="text-right space-y-1">
          {log.weatherText && (
            <div className="text-sm text-gray-700"><span className="font-medium">Weather:</span> {log.weatherText}</div>
          )}
          {log.weatherMode !== "none" && (log.windKph != null || log.humidityPercent != null || log.precipitationMm != null) && (
            <div className="font-weather-details">
              {log.windKph != null && <span>Wind {Math.round(log.windKph)} km/h</span>}
              {log.humidityPercent != null && <span>{log.windKph != null ? ' • ' : ''}Humidity {Math.round(log.humidityPercent)}%</span>}
              {log.precipitationMm != null && <span>{(log.windKph != null || log.humidityPercent != null) ? ' • ' : ''}Precip {Math.round(log.precipitationMm)} mm</span>}
            </div>
          )}
          <div className="text-sm text-gray-600">{log.author}</div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="text-sm text-gray-700"><span className="font-medium">Project:</span> {projectLabel}</div>
          <div className="text-sm text-gray-700 whitespace-pre-wrap">
            <span className="font-medium">Notes:</span> {log.notes || <span></span>}
          </div>
          <div className="text-sm text-gray-700 whitespace-pre-wrap">
            <span className="font-medium">Issues:</span> {log.issues || <span></span>}
          </div>
          {log.attachments.length > 0 && (
            <div className="text-sm text-gray-700">
              <span className="font-medium">Attachments ({log.attachments.length}):</span>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {log.attachments.map((a) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="block group">
                    <div className="w-full h-20 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                      {a.type.startsWith('image/') ? (
                        <ProtectedImage kind="attachment" id={a.id} alt={a.name} className="w-full h-full object-cover" fallback={<div className="text-xs text-gray-500 px-2 text-center truncate">{a.name}</div>} />
                      ) : (
                        <div className="text-xs text-gray-500 group-hover:text-gray-700 px-2 text-center truncate">{a.name}</div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


